// src/hooks/usePlanUsage.js
//
// Hook que consome /users/me/usage e devolve, para a empresa do usuário logado,
// quantos chips/agentes ela já criou vs. o limite do plano em vigor.
//
// Por que existe: a regra de "pode ou não pode adicionar mais" vive no
// backend (app/plan_limits.py). O frontend só precisa renderizar UI com
// base no que recebe — não cabe ao React decidir o limite do plano.
//
// API:
//   const { usage, loading, error, refetch } = usePlanUsage();
//
//   usage = {
//     plan: 'STARTER' | 'BUSINESS' | 'ENTERPRISE',
//     chips:  { used, limit, can_add },
//     agents: { used, limit, can_add },
//   }
//
// Enquanto `loading === true` ou `error` for verdadeiro, os call sites
// devem assumir que NÃO pode adicionar — assim a gente nunca mostra o
// botão antes do backend confirmar, evitando criar chip/agente que
// estoura o limite.
import { useEffect, useState, useCallback } from 'react';
import api from '../services/Api';

export default function usePlanUsage() {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUsage = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await api.get('/users/me/usage', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsage(res.data);
      setError(null);
    } catch (err) {
      // Fallback conservador: trata como "sem permissão para adicionar".
      // Melhor esconder o botão indevidamente do que liberar e estourar o limite.
      setError(err);
      setUsage(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  return { usage, loading, error, refetch: fetchUsage };
}
